/**
 * Chat linkifier: pure tokenizer, no DOM, no HTML. A message body becomes a
 * list of text/link tokens and the RENDERER builds real <a> elements from
 * them — user content never passes through any HTML parser, which is the
 * whole XSS story here (React text nodes stay text nodes).
 *
 * Conservative on purpose: only explicit http(s) URLs. Children type
 * "www.roblox.com" and worse; auto-linking scheme-less text would turn typos
 * into tappable surfaces. A teacher pasting a real lesson link pastes the whole
 * URL. Arabic IS allowed inside a URL — browsers copy Arabic-path Wikipedia
 * links in their pretty Unicode form, and cutting the match at the first
 * Arabic letter would break exactly the links this classroom shares.
 */

export interface LinkToken {
  type: 'text' | 'link'
  value: string
  /** For links only: the href to open (bidi-control-stripped). */
  href?: string
}

/** Explicit-scheme URLs, ending at whitespace/quotes/angle brackets only. */
const URL_PATTERN = /https?:\/\/[^\s<>"'`«»]+/gi

/**
 * Trailing characters that read as sentence punctuation, not URL: Latin and
 * Arabic full stops and question/exclamation marks (، ؛ ؟), ellipses, quotes,
 * and closing brackets — with one exception below for balanced parens, so
 * "…/wiki/Bug_(film)" survives while "(see https://a.com)" sheds the ")".
 */
const TRAILING_PUNCTUATION = new Set([...'.,!?;:…»"\'’”،؛؟]}>'])

/**
 * Unicode bidi control characters (LRM/RLM, embeddings, isolates). Smuggled
 * INTO a URL they can make "evil.com/moc.elgoog" render google-shaped inside
 * RTL text; they are never legal in a URL, so they are stripped from both the
 * display text and the href.
 */
const BIDI_CONTROLS = /[‎‏‪-‮⁦-⁩]/g

function trimTrailing(raw: string): string {
  let url = raw
  for (;;) {
    const last = url[url.length - 1]
    if (!last) return url
    if (last === ')') {
      const opens = (url.match(/\(/g) ?? []).length
      const closes = (url.match(/\)/g) ?? []).length
      if (closes > opens) {
        url = url.slice(0, -1)
        continue
      }
      return url
    }
    if (TRAILING_PUNCTUATION.has(last)) {
      url = url.slice(0, -1)
      continue
    }
    return url
  }
}

/** First http(s) URL in a body — what the preview card unfurls. */
export function firstLink(body: string | null): string | null {
  if (!body) return null
  const token = splitLinks(body).find((candidate) => candidate.type === 'link')
  return token?.href ?? null
}

/**
 * Tokenize a message body. The renderer puts each link label in its own
 * <bdi dir="ltr"> so a URL inside an Arabic sentence reads left-to-right
 * without reordering the sentence around it.
 */
export function splitLinks(body: string): LinkToken[] {
  const tokens: LinkToken[] = []
  let cursor = 0
  URL_PATTERN.lastIndex = 0
  for (;;) {
    const match = URL_PATTERN.exec(body)
    if (!match) break
    const url = trimTrailing(match[0])
    // Too short to be a real destination ("https://a.b" is 11 chars).
    if (url.length < 12) continue
    const start = match.index
    if (start > cursor) tokens.push({ type: 'text', value: body.slice(cursor, start) })
    const clean = url.replace(BIDI_CONTROLS, '')
    tokens.push({ type: 'link', value: clean, href: clean })
    cursor = start + url.length
    // Re-anchor after the trim: trailing punctuation returns to the text run.
    URL_PATTERN.lastIndex = cursor
  }
  if (cursor < body.length) tokens.push({ type: 'text', value: body.slice(cursor) })
  return tokens
}

/**
 * Client-side gate for a preview thumbnail, mirroring the server's rule
 * (defense in depth — if the API ever regresses, the browser still refuses):
 * https only, no credentials, and never an IP-literal host. A hostile image
 * URL here would point every VIEWING child's browser at it — including
 * addresses on the child's own home network, which no server check can see.
 */
export function isSafePreviewImage(url: string | null | undefined): url is string {
  if (!url) return false
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:') return false
  if (parsed.username || parsed.password) return false
  const host = parsed.hostname
  if (host.startsWith('[')) return false // IPv6 literal
  if (/^\d+(\.\d+){3}$/.test(host)) return false // IPv4 literal
  // A host that is ALL digits is an IPv4 address written the long way round:
  // browsers read `https://2130706433/` as 127.0.0.1. Refusing only dotted quads
  // left the same address reachable by spelling it differently.
  if (/^\d+$/.test(host)) return false
  // Likewise `0x7f.1`, `0177.0.0.1` — any host whose labels are all numeric in
  // some base is an address, not a name. Real hostnames have a non-numeric TLD.
  if (/^(0x[\da-f]+|\d+)(\.(0x[\da-f]+|\d+))*$/i.test(host)) return false
  // The loopback names, which resolve to the viewer's own machine. There is no
  // legitimate lesson image on a child's localhost.
  if (host === 'localhost' || host.endsWith('.localhost')) return false
  return true
}
